import { partition } from 'lodash';
import { z } from 'zod';

import { ExperienceImageSizeMediaSchema, MediaSchema, ResourceGroupCollectionEventType } from './common';
import { ScheduleType } from './schedule-time-frame';
import { DateStringSchema, TimeStringSchema } from '../../common';
import { ExperienceVersion } from '../../experience/const';
import { buildQueryString } from '../../utils/querystring';
import { AuctionResponse, AuctionStatus } from '../auction/auction';
import { LanguageCode } from '../brand/brand';
import { makeEndpoint, paginateQuerySchema, paginateResponseSchema } from '../common';
import { ListingPictureSchema } from '../listing/listing';
import { LocationSchema } from '../location/location';
import { ListingPictureKind } from '../media/media';
import { TranslationSchema, TranslationWithIdSchema } from '../translation/types';
export enum ResourceGroupCollectionKind {
  EVENT = 'event',
  RESERVE = 'reserve',
}

export enum ResourceGroupCollectionStatus {
  PUBLISHED = 'published',
  UNPUBLISHED = 'unpublished',
  ARCHIVED = 'archived',
  DRAFT = 'draft',
}

// Schema for auction response in resource group collections with publishedStatus
export const ResourceGroupCollectionAuctionResponse = AuctionResponse.extend({
  publishedStatus: z.nativeEnum(AuctionStatus).nullable(),
  winningAmount: z.string().nullable(),
  winningBidderId: z.string().uuid().nullable(),
  bidsCount: z.number(),
  uniqueBiddersCount: z.number(),
});

export type ResourceGroupCollectionAuctionResponse = z.infer<typeof ResourceGroupCollectionAuctionResponse>;

export const ResourceGroupCollectionSchema = z.object({
  id: z.string().uuid(),
  order: z.number().finite().nullable(),
  brandId: z.string().uuid(),
  status: z.nativeEnum(ResourceGroupCollectionStatus),
  version: z.nativeEnum(ExperienceVersion),
  map: ExperienceImageSizeMediaSchema.nullable(),
  locationId: z.string().uuid(),
  location: LocationSchema,
  category: z.object({ id: z.string().uuid(), name: z.string() }),
  categoryId: z.string().uuid(),
  vibes: z
    .array(z.object({ id: z.string().uuid(), name: z.string(), order: z.number().nullable() }))
    .min(1)
    .max(3, { message: 'A collection can have a maximum of 3 vibes' }),
  vibeIds: z.array(z.string().uuid()).min(1).max(3),
  isMapShown: z.boolean(),
  scheduleType: z.nativeEnum(ScheduleType).optional(),
  kind: z.nativeEnum(ResourceGroupCollectionKind).default(ResourceGroupCollectionKind.RESERVE), // TODO : remove the default value
  mapId: z.string().uuid().nullable(),
  pictureIds: z.array(z.string().uuid()).max(7),
  pictures: z.array(ListingPictureSchema),
  name: z.string().trim().nonempty(),
  summary: z.string().nullable(),
  details: z.string().nullable(),
  agenda: z.string().nullable().optional(),
  reminders: z.string().nullable().optional(),
  menu: z.string().nullable().optional(),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
  publishedAt: z.string().datetime().nullable(),
  archivedAt: z.string().datetime().nullable(),
  dates: z.array(DateStringSchema).nullable().optional(),
  startTimes: z.array(TimeStringSchema).nullable().optional(),
  waitlistId: z.string().uuid().nullable(),
  eventType: z.nativeEnum(ResourceGroupCollectionEventType).nullable(),
  cancellationPolicyId: z.string().uuid().nullable(),
  auction: ResourceGroupCollectionAuctionResponse.nullable().optional(),
  reschedulePolicyId: z.string().uuid().nullable(),
  limitedTimeId: z.string().uuid().nullable(),
  isUnlisted: z.boolean().default(false),
  customCallToAction: z.string().nullable(),
});

export const ResourceGroupCollectionResponseSchema = ResourceGroupCollectionSchema.omit({
  mapId: true,
  pictureIds: true,
  categoryId: true,
  vibeIds: true,
}).extend({
  // Full public slug (`sluggify(name)-slugId`) for building `?collectionId=` / `?eventId=` URLs.
  // null when the collection has no slugId yet (pre-dates the slug feature and hasn't been saved and
  // published since).
  slug: z.string().nullable(),
  vibes: ResourceGroupCollectionSchema.shape.vibes.min(0),
  medias: z
    .array(
      MediaSchema.omit({
        kind: true,
      }).extend({
        kind: z.nativeEnum(ListingPictureKind),
      }),
    )
    .optional(),
});

export type ResourceGroupCollectionResponse = z.infer<typeof ResourceGroupCollectionResponseSchema>;

export const PostResourceGroupCollectionEndpoint = makeEndpoint({
  method: 'POST',
  url: ({ brandId }: { brandId: string }) => `/v1/brands/${brandId}/resource-group-collections`,
  requestSchema: ResourceGroupCollectionSchema.pick({
    order: true,
    categoryId: true,
    vibeIds: true,
    isMapShown: true,
    scheduleType: true,
    pictureIds: true,
    mapId: true,
    name: true,
    kind: true,
    summary: true,
    details: true,
    agenda: true,
    reminders: true,
    menu: true,
    dates: true,
    startTimes: true,
    eventType: true,
    cancellationPolicyId: true,
    reschedulePolicyId: true,
    limitedTimeId: true,
    isUnlisted: true,
    customCallToAction: true,
  })
    .partial({
      eventType: true,
      scheduleType: true,
      pictureIds: true,
      cancellationPolicyId: true,
      reschedulePolicyId: true,
      limitedTimeId: true,
    })
    .extend({
      location: z.object({
        latitude: z.number(),
        name: z.string(),
        longitude: z.number(),
        additionalInfo: z.string().optional(),
      }),
      medias: z
        .array(
          MediaSchema.omit({
            kind: true,
          }).extend({
            kind: z.nativeEnum(ListingPictureKind),
          }),
        )
        .refine(
          (medias) => {
            const [covers, gallery] = partition(medias, (picture) => picture.kind === ListingPictureKind.COVER);
            return covers.length <= 3 && gallery.length <= 8;
          },
          {
            message: 'At most 3 cover medias and 8 gallery medias',
          },
        )
        .optional(),
      pictures: z
        .array(
          z.object({
            id: z.string().uuid(),
            kind: z.nativeEnum(ListingPictureKind).optional(),
          }),
        )
        .refine(
          (pictures) => {
            const [covers, gallery] = partition(pictures, (picture) => picture.kind === ListingPictureKind.COVER);
            return covers.length <= 3 && gallery.length <= 8;
          },
          {
            message: 'At most 3 cover pictures and 8 gallery pictures',
          },
        )
        .optional(),
    })
    .strict(),
  responseSchema: ResourceGroupCollectionResponseSchema,
});

export const GetResourceGroupCollectionsQuerySchema = paginateQuerySchema().merge(
  z.object({
    filters: z
      .array(
        z.object({
          operator: z.literal('equals'),
          name: z.literal('status'),
          value: z.nativeEnum(ExperienceVersion),
        }),
      )
      .optional(),
  }),
);
export type GetResourceGroupCollectionsQuery = z.infer<typeof GetResourceGroupCollectionsQuerySchema>;

export const GetResourceGroupCollectionsEndpoint = makeEndpoint({
  method: 'GET',
  url: ({ brandId, page, limit }: { brandId: string; page: number; limit: number }) =>
    `/v1/brands/${brandId}/resource-group-collections?page=${page}&limit=${limit}`,
  requestSchema: z.void(),
  responseSchema: paginateResponseSchema(ResourceGroupCollectionResponseSchema).strict(),
});

const GetResourceGroupCollectionQuerySchema = z.object({
  version: z.nativeEnum(ExperienceVersion).default(ExperienceVersion.LATEST),
  language: z.nativeEnum(LanguageCode).optional(),
});

export const DetailedResourceGroupCollectionSchema = ResourceGroupCollectionResponseSchema.extend({
  status: z.nativeEnum(ResourceGroupCollectionStatus),
});

export type DetailedResourceGroupCollectionResponse = z.infer<typeof DetailedResourceGroupCollectionSchema>;

export const GetResourceGroupCollectionEndpoint = makeEndpoint({
  method: 'GET',
  url: ({
    brandId,
    resourceGroupCollectionId,
    query,
  }: {
    brandId: string;
    resourceGroupCollectionId: string;
    query: z.input<typeof GetResourceGroupCollectionQuerySchema>;
  }) => `/v1/brands/${brandId}/resource-group-collections/${resourceGroupCollectionId}?${buildQueryString(query)}`,
  requestSchema: z.void(),
  responseSchema: DetailedResourceGroupCollectionSchema,
  querySchema: GetResourceGroupCollectionQuerySchema,
});

const PatchResourceGroupCollectionRequestSchema = PostResourceGroupCollectionEndpoint.requestSchema
  .omit({ eventType: true })
  .extend({ archived: z.boolean() })
  .partial()
  .strict();

export const PatchResourceGroupCollectionEndpoint = makeEndpoint({
  method: 'PATCH',
  url: ({ resourceGroupCollectionId, brandId }: { resourceGroupCollectionId: string; brandId: string }) =>
    `/v1/brands/${brandId}/resource-group-collections/${resourceGroupCollectionId}`,
  requestSchema: PatchResourceGroupCollectionRequestSchema,
  responseSchema: ResourceGroupCollectionResponseSchema.strict(),
});

export const ArchiveResourceGroupCollectionEndpoint = makeEndpoint({
  method: 'PATCH',
  url: ({ resourceGroupCollectionId, brandId }: { resourceGroupCollectionId: string; brandId: string }) =>
    `/v1/brands/${brandId}/resource-group-collections/${resourceGroupCollectionId}/archive`,
  requestSchema: z.void(),
  responseSchema: z.void(),
});

export const UnarchiveResourceGroupCollectionEndpoint = makeEndpoint({
  method: 'PATCH',
  url: ({ resourceGroupCollectionId, brandId }: { resourceGroupCollectionId: string; brandId: string }) =>
    `/v1/brands/${brandId}/resource-group-collections/${resourceGroupCollectionId}/unarchive`,
  requestSchema: z.void(),
  responseSchema: z.void(),
});

export const DeleteResourceGroupCollectionEndpoint = makeEndpoint({
  method: 'DELETE',
  url: ({ resourceGroupCollectionId, brandId }: { resourceGroupCollectionId: string; brandId: string }) =>
    `/v1/brands/${brandId}/resource-group-collections/${resourceGroupCollectionId}`,
  requestSchema: z.void(),
  responseSchema: z.void(),
});

export const PublishResourceGroupCollectionEndpoint = makeEndpoint({
  method: 'POST',
  url: ({ resourceGroupCollectionId, brandId }: { resourceGroupCollectionId: string; brandId: string }) =>
    `/v1/brands/${brandId}/resource-group-collections/${resourceGroupCollectionId}/publish`,
  requestSchema: z.void(),
  responseSchema: ResourceGroupCollectionResponseSchema.strict(),
});

export const UnpublishResourceGroupCollectionEndpoint = makeEndpoint({
  method: 'DELETE',
  url: ({ resourceGroupCollectionId, brandId }: { resourceGroupCollectionId: string; brandId: string }) =>
    `/v1/brands/${brandId}/resource-group-collections/${resourceGroupCollectionId}/publish`,
  requestSchema: z.void(),
  responseSchema: z.void(),
});

const DateQuerySchema = z.object({
  eventDate: DateStringSchema,
  eventTime: TimeStringSchema.optional(),
});

const QRCodeScanPayloadSchema = z.object({
  qrCodePayload: z.string(),
});

const ParticipantScanResponse = z.object({
  participantName: z.string(),
  ticketNumber: z.string(),
  priceLabel: z.string(),
  confirmationCode: z.string(),
  eventDate: DateStringSchema,
  eventTime: TimeStringSchema,
});

export const CollectionEventParticipantScanEndpoint = makeEndpoint({
  method: 'POST',
  url: ({
    brandId,
    resourceGroupCollectionId,
    query,
  }: {
    brandId: string;
    resourceGroupCollectionId: string;
    query: z.infer<typeof DateQuerySchema>;
  }) =>
    `/v1/brands/${brandId}/resource-group-collections/${resourceGroupCollectionId}/participants/scan?${buildQueryString(
      query,
    )}`,
  requestSchema: QRCodeScanPayloadSchema,
  responseSchema: ParticipantScanResponse,
  querySchema: DateQuerySchema,
});

const CloneResourceGroupCollectionResponseSchema = z.object({ resourceGroupCollectionId: z.string().uuid() });

export const CloneResourceGroupCollectionEndpoint = makeEndpoint({
  method: 'POST',
  url: ({ brandId, resourceGroupCollectionId }: { brandId: string; resourceGroupCollectionId: string }) =>
    `/v1/brands/${brandId}/resource-group-collections/${resourceGroupCollectionId}/clone`,
  requestSchema: z.void(),
  responseSchema: CloneResourceGroupCollectionResponseSchema,
});

export const GetTranslationsForResourceGroupCollectionQuerySchema = z.object({
  language: z.nativeEnum(LanguageCode).optional(),
});

export const GetTranslationsForResourceGroupCollectionEndpoint = makeEndpoint({
  method: 'GET',
  url: ({
    brandId,
    resourceGroupCollectionId,
    version,
    query,
  }: {
    brandId: string;
    resourceGroupCollectionId: string;
    version: ExperienceVersion;
    query: z.input<typeof GetTranslationsForResourceGroupCollectionQuerySchema>;
  }) =>
    `/v1/brands/${brandId}/resource-group-collections/${resourceGroupCollectionId}/version/${version}/i18n?${buildQueryString(
      query,
    )}`,
  responseSchema: z.object({
    translations: z.object({
      resourceGroupCollection: z.array(TranslationWithIdSchema),
      listingAddOns: z.array(TranslationWithIdSchema),
      cancellationPolicy: z.array(TranslationWithIdSchema),
      reschedulePolicy: z.array(TranslationWithIdSchema),
      location: z.array(TranslationWithIdSchema),
    }),
  }),
  requestSchema: z.void(),
  querySchema: GetTranslationsForResourceGroupCollectionQuerySchema,
});

export const PatchTranslationsForResourceGroupCollectionEndpoint = makeEndpoint({
  method: 'PATCH',
  url: ({
    brandId,
    resourceGroupCollectionId,
    version,
  }: {
    brandId: string;
    resourceGroupCollectionId: string;
    version: ExperienceVersion;
  }) => `/v1/brands/${brandId}/resource-group-collections/${resourceGroupCollectionId}/version/${version}/i18n`,
  responseSchema: z.void(),
  requestSchema: z.object({
    translations: z.array(TranslationSchema.omit({ createdAt: true, updatedAt: true })),
  }),
});

export const TranslateResourceGroupCollectionEndpoint = makeEndpoint({
  method: 'POST',
  url: ({ brandId, resourceGroupCollectionId }: { brandId: string; resourceGroupCollectionId: string }) =>
    `/v1/brands/${brandId}/resource-group-collections/${resourceGroupCollectionId}/translate`,
  responseSchema: z.void(),
  requestSchema: z.void(),
});

export const ReorderResourceGroupsEndpoint = makeEndpoint({
  method: 'PATCH',
  url: ({ brandId, resourceGroupCollectionId }: { brandId: string; resourceGroupCollectionId: string }) =>
    `/v1/brands/${brandId}/resource-group-collections/${resourceGroupCollectionId}/resource-groups/reorder`,
  requestSchema: z.object({
    resourceGroups: z
      .array(
        z.object({
          id: z.string().uuid(),
          version: z.nativeEnum(ExperienceVersion),
          order: z.number().int(),
        }),
      )
      .min(1),
  }),
  responseSchema: z.void(),
});

export enum ResourceGroupCollectionField {
  NAME = 'name',
  SUMMARY = 'summary',
  DETAILS = 'details',
  AGENDA = 'agenda',
  MENU = 'menu',
  REMINDERS = 'reminders',
  CUSTOM_CALL_TO_ACTION = 'customCallToAction',
}

// First Available Date
const GetCollectionFirstAvailableDateQuerySchema = z.object({
  from: DateStringSchema.nullable().optional(),
  to: DateStringSchema.nullable().optional(),
  private: z.coerce.boolean().default(false),
  latest: z.coerce.boolean().default(false),
});

const PriceTierSchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
  price: z.number(),
  maxQuantity: z.number().int().positive().nullable().optional(),
});

const CollectionFirstAvailableDateSessionSchema = z.object({
  startTime: TimeStringSchema,
  duration: z.number().int().positive(),
  supportedParticipantsCount: z.number().int().nonnegative(),
  priceTiers: z.array(PriceTierSchema),
});

const ResourceGroupFirstAvailableDateSchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
  order: z.number(),
  firstAvailableDate: z
    .object({
      scheduledDate: DateStringSchema,
      sessions: z.array(CollectionFirstAvailableDateSessionSchema),
    })
    .nullable(),
});

const CollectionFirstAvailableDateResponseSchema = z.object({
  firstAvailableDate: DateStringSchema.nullable(),
  resourceGroups: z.array(ResourceGroupFirstAvailableDateSchema),
});

export type CollectionFirstAvailableDateResponse = z.infer<typeof CollectionFirstAvailableDateResponseSchema>;

export const GetCollectionFirstAvailableDateEndpoint = makeEndpoint({
  method: 'GET',
  url: ({
    resourceGroupCollectionId,
    query,
  }: {
    brandId: string;
    resourceGroupCollectionId: string;
    query: z.input<typeof GetCollectionFirstAvailableDateQuerySchema>;
  }) => `/v2/resource-group-collections/${resourceGroupCollectionId}/first-available-date?${buildQueryString(query)}`,
  requestSchema: z.void(),
  responseSchema: CollectionFirstAvailableDateResponseSchema,
  querySchema: GetCollectionFirstAvailableDateQuerySchema,
});

export const GetCollectionFirstAvailableDateInternalEndpoint = makeEndpoint({
  method: 'GET',
  url: ({
    brandId,
    resourceGroupCollectionId,
    query,
  }: {
    brandId: string;
    resourceGroupCollectionId: string;
    query: z.input<typeof GetCollectionFirstAvailableDateQuerySchema>;
  }) =>
    `/v1/brands/${brandId}/resource-group-collections/${resourceGroupCollectionId}/first-available-date?${buildQueryString(
      query,
    )}`,
  requestSchema: z.void(),
  responseSchema: CollectionFirstAvailableDateResponseSchema,
  querySchema: GetCollectionFirstAvailableDateQuerySchema,
});
